import { useMemo, useRef } from "react";

import { compileSchema } from "./compile";
import { SchemaFormContext } from "./context";
import { Section } from "./fields/Section";
import { Schema, SchemaError } from "./types";
import { errorsByPath } from "./validation";

/*
 * Renders a JSON Schema as a form bound to react-hook-form at `rootPath`.
 * Must be rendered inside a `FormProvider`.
 * Pass the errors of `useSchemaValidation` to show them next to the fields.
 * `stored` is the record as loaded, so secret fields know which masks keep a stored secret.
 */
export const SchemaForm = ({
  schema,
  rootPath,
  errors = [],
  stored,
}: {
  schema: Schema;
  rootPath: string;
  errors?: SchemaError[];
  stored?: unknown;
}) => {
  const root = useMemo(() => compileSchema(schema), [schema]);
  const valueCache = useRef<Record<string, unknown>>({});
  const context = useMemo(
    () => ({
      schema,
      rootPath,
      stored,
      errors: errorsByPath(errors),
      valueCache,
    }),
    [schema, rootPath, stored, errors],
  );

  return (
    <SchemaFormContext.Provider value={context}>
      {root.children.map((node, index) => (
        <Section key={node.path} node={node} index={index} />
      ))}
    </SchemaFormContext.Provider>
  );
};
